import React, { ReactNode, createContext, useCallback, useContext, useMemo } from "react";
import { VatReport } from "../../../model/accounting/vat/VatReport.js";
import { Month } from "../../../model/accounting/date/Month.js";
import { getCurrentYear } from "../../../model/accounting/date/getCurrentYear.js";
import { getCurrentMonth } from "../../../model/accounting/date/getCurrentMonth.js";
import { fetchVatReport } from "../accountingApi.js";
import { useParameters } from "../../../../packages/router/params/useParameters.js";
import { useHref } from "../../../../packages/router/href/HrefProvider.js";
import { useLoadEffect } from "../../../../react/load/useLoadEffect.js";

type VatReportState = {
  year: string;
  month: Month;
  setYear: (year: string) => void;
  setMonth: (month: Month) => void;
  report?: VatReport;
};

const VatReportContext = createContext<VatReportState | undefined>(undefined);

type Props = {
  children: ReactNode;
};

/**
 * VAT report state provider.
 */
export const VatReportProvider = ({ children }: Props) => {
  const params = useParameters<{ year: string; month: string }>();
  const { setHref } = useHref();

  const year = useMemo(() => (params.year ? params.year : getCurrentYear()), [params.year]);
  const month = useMemo(() => (params.month ? (params.month as Month) : getCurrentMonth()), [params.month]);

  const setYear = useCallback((year: string) => setHref(`/vat/${year}/${month}`), [month, setHref]);
  const setMonth = useCallback((month: Month) => setHref(`/vat/${year}/${month}`), [setHref, year]);

  const result = useLoadEffect(`vat-report-${year}-${month}`, () => fetchVatReport(year, month));

  const value: VatReportState = {
    year,
    month,
    setYear,
    setMonth,
    report: result.state === "success" ? result.data : undefined
  };

  return <VatReportContext.Provider value={value}>{children}</VatReportContext.Provider>;
};

/**
 * Helper hook to access the VAT report state.
 */
export const useVatReport = () => {
  const context = useContext(VatReportContext);
  if (!context) {
    throw new Error("The VAT report context is not provided.");
  }
  return context;
};
